import { DISABLED, link } from "@/components/Action.tsx";
import { Toolbar } from "@/components/Toolbar.tsx";
import { WModalButton, WToolButton } from "@/components/button/WToolButton.tsx";
import { WModal, WModalContent } from "@/components/modal/WModal.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { BootFromIsoModal } from "@/modules/compute/BootFromIsoModal.tsx";
import { ChangePasswordModal } from "@/modules/compute/ChangePasswordModal.tsx";
import { ReinstallModal } from "@/modules/compute/ReinstallModal.tsx";
import { VmCloneModal } from "@/modules/compute/VmCloneModal.tsx";
import { VmResizeToolbarButton } from "@/modules/compute/VmResizeModal.tsx";
import type { VirtualMachineWithAssigned } from "@/modules/compute/joinAssignedQuery.ts";
import { osIsWindows } from "@/modules/compute/os/os.ts";
import { startVmMutation, stopVmMutation } from "@/modules/compute/vmQuery.ts";
import { downloadBlob } from "@/utils/downloadBlob.tsx";
import { Trans, useLingui } from "@lingui/react/macro";
import { type ReactNode, useState } from "react";

export interface VmToolbarProps {
    vm: VirtualMachineWithAssigned;
    deleteModal: ReactNode;
}

export function VmToolbar({ vm, deleteModal }: VmToolbarProps) {
    //#region Hooks
    const { t } = useLingui();
    const startMutation = useStandardMutation(startVmMutation);
    const stopMutation = useStandardMutation(stopVmMutation);

    const [rebootInProgress, setRebootInProgress] = useState(false);
    //#endregion

    const { name, status, os_name, username, uuid, location, hostname, assignedPublicIp } = vm;
    const address = assignedPublicIp?.address;

    return (
        <Toolbar>
            <WToolButton
                label={t`Start`}
                icon="jp-icon-run"
                isDisabled={status !== "stopped"}
                action={async () => {
                    await startMutation.mutateAsync({ uuid, location });
                }}
            />

            <WModal button={<WModalButton label={t`Stop`} icon="jp-icon-stop" isDisabled={status !== "running"} />}>
                <WModalContent
                    title={t`Stop`}
                    label={t`Stop`}
                    modalAction={async () => {
                        await stopMutation.mutateAsync({ uuid, location, force: false });
                    }}
                >
                    <Trans>
                        This will shut down your resource "{name}". If graceful shutdown does not finish in 120 seconds,
                        then a forceful stop will be carried out. For stopped resources you will only be charged for the
                        storage.
                    </Trans>
                </WModalContent>
            </WModal>

            <WModal
                button={
                    <WModalButton
                        label={<Trans comment="Unsafely shut down the virtual machine">Force stop</Trans>}
                        icon="jp-power-icon"
                        isDisabled={status === "stopped"}
                    />
                }
            >
                <WModalContent
                    title={t`Force stop`}
                    label={t`Force stop`}
                    modalAction={async () => {
                        await stopMutation.mutateAsync({ uuid, location, force: true });
                    }}
                >
                    <Trans>
                        This will forcefully shut down your resource "{name}". For stopped resources you will only be
                        charged for the storage.
                    </Trans>
                </WModalContent>
            </WModal>

            <WModal
                button={<WModalButton label={t`Reboot`} icon="jp-time-reverse-icon" isDisabled={rebootInProgress} />}
            >
                <WModalContent
                    title={t`Reboot`}
                    label={t`Reboot`}
                    modalAction={async () => {
                        setRebootInProgress(true);
                        await stopMutation.mutateAsync({ uuid, location, force: true });
                        await startMutation.mutateAsync({ uuid, location });
                        setRebootInProgress(false);
                    }}
                >
                    <Trans>This will reboot your resource "{name}".</Trans>
                </WModalContent>
            </WModal>

            <VmCloneModal vm={vm} />

            <WToolButton
                label={t`Terminal`}
                icon="jp-monitor-icon"
                action={link({
                    to: "/console/$location/$vmId",
                    params: { location, vmId: uuid },
                    target: "_blank",
                })}
            />

            <WToolButton
                label={osIsWindows(os_name) ? t`RDP link` : t`SSH link`}
                icon="jp-icon-terminal"
                action={
                    osIsWindows(os_name)
                        ? address
                            ? () => downloadRdp(address, hostname)
                            : DISABLED
                        : `ssh://${username}@${assignedPublicIp?.address}`
                }
            />

            <VmResizeToolbarButton vm={vm} />

            <ChangePasswordModal vm={vm} />

            <BootFromIsoModal vm={vm} />

            <ReinstallModal vm={vm} />

            {deleteModal}
        </Toolbar>
    );
}

function downloadRdp(address: string, namePrefix: string, username = "administrator") {
    const rdpContent = `
full address:s:${address}
username:s:${username}
`;
    const blob = new Blob([rdpContent], { type: "application/x-rdp" });
    downloadBlob(blob, `${namePrefix}_remote_connection.rdp`);
}
